import { useSyncExternalStore } from "react"
import type { VoiceConnectionStatus } from "@/types/voice-chat-rtc"

import { useAppSelector } from "@/lib/storage/hooks"
import {
  selectAssistantAudioPlaying,
  selectVoiceConnecting,
} from "@/lib/storage/voice-chat-slice"
import {
  useVoiceAudioLevel,
  type VoiceAudioLevels,
  type VoiceLevelStore,
} from "./use-voice-audio-level"
import { useVoiceSyntheticPulse } from "./use-voice-synthetic-pulse"

export interface VoiceLevels {
  // Assistant playback is actually being heard right now.
  audible: boolean
  // The levels themselves, read outside render. `get` builds a fresh object
  // per call, so it is not a useSyncExternalStore snapshot.
  live: VoiceLevelStore<{
    // 0..1 assistant playback level — real when the browser meters it,
    // synthetic otherwise. Only meaningful while `audible`.
    assistantAmplitude: number
    // 0..1 mic level. Flat zero until the session is connected — see below —
    // and otherwise ungated: a muted mic reports ~0 on its own.
    mic: number
  }>
}

const clamp01 = (level: number) => Math.min(Math.max(level, 0), 1)

/**
 * The metered-or-synthetic rule, in exactly one place: voice mode composes it
 * over the pooled sampler below, the bounded interview over its own
 * (use-voice-interview-levels.ts).
 *
 * Audibility tracks playback, not the speaking status: status flips at
 * `response.created`, but a slow-to-generate turn only becomes audible at
 * `output_audio_buffer.started` — keying off status left visualizations
 * sitting at rest, visibly late against the first spoken words.
 */
export function useVoiceLevelsFrom(
  status: VoiceConnectionStatus,
  audioPlaying: boolean,
  meter: VoiceLevelStore<VoiceAudioLevels>
): VoiceLevels {
  const audible = status === "assistant_speaking" && audioPlaying
  // A latch rather than a level, so this re-renders once a session at most.
  const metered = useSyncExternalStore(
    meter.subscribe,
    () => meter.get().assistantMetered,
    () => false
  )
  // Synthetic pulse only when metering is unavailable, so real metering never
  // runs a needless rAF loop.
  const pulse = useVoiceSyntheticPulse(audible && !metered)

  return {
    audible,
    live: {
      get: () => {
        const levels = meter.get()
        return {
          assistantAmplitude: levels.assistantMetered
            ? clamp01(levels.assistant)
            : pulse.get(),
          mic: clamp01(levels.mic),
        }
      },
      subscribe: (onChange) => {
        const unsubscribeMeter = meter.subscribe(onChange)
        const unsubscribePulse = pulse.subscribe(onChange)
        return () => {
          unsubscribeMeter()
          unsubscribePulse()
        }
      },
    },
  }
}

/**
 * What every voice visualization needs: is the assistant audible, and how loud.
 * Shared by every surface that moves with the voice — the orb (halo + mic-driven
 * disc) above all.
 *
 * The samplers underneath are multiplexed, so calling this from several surfaces
 * at once costs one getStats poll (see useVoiceAudioLevel).
 */
export function useVoiceLevels(status: VoiceConnectionStatus): VoiceLevels {
  const audioPlaying = useAppSelector(selectAssistantAudioPlaying)

  // Nothing is metered until the session is up. The mic goes live when
  // getUserMedia resolves, which is well before anything can hear it, so
  // metering across the connect window had the orb answering speech that was
  // going nowhere — a visual "I'm listening" over a session that isn't. The
  // connect window is the same one the orb's grey treatment runs on, so the
  // orb starts reacting exactly when it stops looking like it's connecting.
  const connecting = useAppSelector(selectVoiceConnecting)
  const meter = useVoiceAudioLevel(!connecting && status !== "error")

  return useVoiceLevelsFrom(status, audioPlaying, meter)
}
